import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { useEffect, useRef } from 'react';
import { EBuzzoolaAnalyticsPayloadType } from '../enums/EBuzzoolaAnalyticsPayloadType';
import { TeaserWebBuzzoolaEvent } from '../services/TeaserWebBuzzoolaEvent';
import { useConstant } from './useConstant';
import { useIntersectionObserver } from './useIntersectionObserver';
export function useBuzzoolaPostViewAnalytics(_ref) {
  let {
    initialBuzzoolaAnalyticsPayload,
    elementRef,
    traceId,
    callbacks: {
      onClick,
      onView,
      onView1,
      onView2,
      onLoad,
      onError,
      onStub,
      onInternalError
    }
  } = _ref;
  const timeoutsRef = useRef({
    view1: null,
    view2: null
  });
  const teaserWebBuzzoolaEvent = useConstant(() => {
    const payload = _objectSpread(_objectSpread({}, initialBuzzoolaAnalyticsPayload), {}, {
      bannerSource: 'buzzoola',
      traceId,
      type: EBuzzoolaAnalyticsPayloadType.WithoutBuzzoolaData
    });
    return new TeaserWebBuzzoolaEvent(payload);
  });
  const {
    subscribeState: subscribeState50PercentVisibility
  } = useIntersectionObserver({
    elementRef,
    initSubscribeAvailable: false,
    observerOptions: {
      threshold: 0.5
    },
    onInViewport(_ref2) {
      let {
        subscribeState: {
          setSubscribeUnavailable
        }
      } = _ref2;
      timeoutsRef.current.view1 = setTimeout(() => {
        timeoutsRef.current.view2 = setTimeout(() => {
          onView2 === null || onView2 === void 0 || onView2();
          teaserWebBuzzoolaEvent.send('show2');
          setSubscribeUnavailable();
        }, 1000);
        onView1 === null || onView1 === void 0 || onView1();
        teaserWebBuzzoolaEvent.send('show1');
      }, 1000);
    },
    onOutViewport(_ref3) {
      let {
        observer,
        subscribeState: {
          setSubscribeUnavailable
        },
        intersectionsCount
      } = _ref3;
      if (intersectionsCount.inView) {
        observer.disconnect();
        setSubscribeUnavailable();
        clearTimeouts(timeoutsRef);
      }
    }
  });
  const {
    subscribeState: subscribeState1PercentVisibility
  } = useIntersectionObserver({
    elementRef,
    initSubscribeAvailable: false,
    observerOptions: {
      threshold: 0.01
    },
    onInViewport(_ref4) {
      let {
        observer,
        subscribeState: {
          setSubscribeUnavailable
        }
      } = _ref4;
      teaserWebBuzzoolaEvent.send('show_1px');
      onView === null || onView === void 0 || onView();
      observer.disconnect();
      setSubscribeUnavailable();
      subscribeState50PercentVisibility.setSubscribeAvailable();
    }
  });

  /**
   * @description Вызывается при клике на баннер
   */
  const handleClick = (adUnit, player, slugname) => {
    teaserWebBuzzoolaEvent.send('click');
    onClick === null || onClick === void 0 || onClick(adUnit, player, slugname);
  };

  /**
   * @description Вызывается когда баннер загрузился
   */
  const handleLoad = (adUnit, player) => {
    var _player$creative$cont;
    const finalBuzzoolaAnalyticsPayload = _objectSpread(_objectSpread({}, teaserWebBuzzoolaEvent.payload), {}, {
      campaignId: player.creative.campaignId,
      creativeId: player.creative.creativeId,
      description: player.creative.description,
      hitUrl: player.creative.clickthroughUrl,
      jsonFields: ((_player$creative$cont = player.creative.content) === null || _player$creative$cont === void 0 ? void 0 : _player$creative$cont.publisherMeta) || {},
      title: player.creative.title,
      type: EBuzzoolaAnalyticsPayloadType.WithBuzzoolaData
    });
    teaserWebBuzzoolaEvent.payload = finalBuzzoolaAnalyticsPayload;
    teaserWebBuzzoolaEvent.send('load');
    subscribeState1PercentVisibility.setSubscribeAvailable();
    onLoad === null || onLoad === void 0 || onLoad(adUnit, player);
  };

  /**
   * @description Вызывается когда баннер был запрошен, но подходящего баннера не нашлось, будет пустое место без баннера
   */
  const handleStub = (adUnit, player) => {
    teaserWebBuzzoolaEvent.send('stub');
    onStub === null || onStub === void 0 || onStub(adUnit, player);
  };

  /**
   * @description Вызывается при ошибке на стороне сервиса Buzzoola
   */
  const handleError = (error, adUnit, player) => {
    teaserWebBuzzoolaEvent.payload = _objectSpread(_objectSpread({}, teaserWebBuzzoolaEvent.payload), {}, {
      errorMessage: error.message,
      errorName: error.name,
      errorType: 'BuzzoolaBannerError',
      type: EBuzzoolaAnalyticsPayloadType.WithBuzzoolaErrorData
    });
    teaserWebBuzzoolaEvent.send('error');
    onError === null || onError === void 0 || onError(error, adUnit, player);
  };

  /**
   * @description Вызывается при ошибке на нашей стороне
   */
  const handleInternalError = error => {
    teaserWebBuzzoolaEvent.payload = _objectSpread(_objectSpread({}, teaserWebBuzzoolaEvent.payload), {}, {
      errorMessage: error.errorMessage,
      errorName: error.errorName,
      errorType: error.errorType,
      type: EBuzzoolaAnalyticsPayloadType.WithBuzzoolaErrorData
    });
    teaserWebBuzzoolaEvent.send('error');
    onInternalError === null || onInternalError === void 0 || onInternalError(error);
  };
  useEffect(() => {
    return () => {
      clearTimeouts(timeoutsRef);
    };
  }, []);
  return {
    onClick: handleClick,
    onError: handleError,
    onInternalError: handleInternalError,
    onLoad: handleLoad,
    onStub: handleStub
  };
}
function clearTimeouts(timeoutsRef) {
  if (timeoutsRef.current.view1 !== null) {
    clearTimeout(timeoutsRef.current.view1);
    timeoutsRef.current.view1 = null;
  }
  if (timeoutsRef.current.view2 !== null) {
    clearTimeout(timeoutsRef.current.view2);
    timeoutsRef.current.view2 = null;
  }
}